import React, { useState, useEffect, useContext } from "react";
import { Typography, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { formatDistance, subDays } from "date-fns";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import PageLoader from "../../components/PageLoader/PageLoader";
import { publicApi } from "../../api/http";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import HomeConsensusGraph from "../../components/HomeConsensusGraph/HomeConsensusGraph";
import HomeDraftOddsBoard from "../../components/HomeDraftOddsBoard/HomeDraftOddsBoard";
import { Container } from "@mui/system";

import "./Home.css";

const TOP_PROSPECTS_BAR_COUNT = 12;
const HOME_DRAFT_ORDER_COUNT = 16;

function PanelHeader({ title, to, linkLabel = "View all" }) {
  return (
    <div className="home-panel-header">
      <Typography variant="h4" component="h2" className="home-panel-title">
        {title}
      </Typography>
      {to && (
        <Link to={to} className="home-panel-link">
          {linkLabel}
          <ArrowForwardIosIcon />
        </Link>
      )}
    </div>
  );
}

function TrendDelta({ delta }) {
  if (delta > 10) {
    return (
      <span className="rank-increase">
        <KeyboardDoubleArrowUpIcon /> {delta}
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span className="rank-increase">
        <KeyboardArrowUpIcon /> {delta}
      </span>
    );
  }
  if (delta >= -10) {
    return (
      <span className="rank-drop">
        <KeyboardArrowDownIcon /> {delta}
      </span>
    );
  }
  return (
    <span className="rank-drop">
      <KeyboardDoubleArrowDownIcon /> {delta}
    </span>
  );
}

function DraftOrderPick({ pick, draftCompleted }) {
  return (
    <div className="draft-card draft-card--compact">
      <span className="draft-card-number">{pick.pick}</span>
      <div className="draft-card-body">
        {draftCompleted && (
          <Link to={`/player/${pick.player_id}`} className="inline-draft-text">
            <img
              src={pick.drafted_team_logo ?? pick.drafting_team_logo ?? pick.original_team_logo}
              alt=""
              className="team-logo"
            />
            <span className="draft-card-text">
              {pick.first_name} {pick.last_name}
            </span>
          </Link>
        )}
        {!draftCompleted && !pick.drafting_team_name && (
          <Link to={`/team/${pick.original_team_id}`} className="inline-draft-text">
            <img src={pick.original_team_logo} alt="" className="team-logo" />
            <span className="draft-card-text">{pick.original_team_name}</span>
          </Link>
        )}
        {!draftCompleted && pick.drafting_team_name && (
          <div className="inline-draft-text">
            <Link to={`/team/${pick.drafting_team_id}`} className="draft-card-team-link">
              <img src={pick.drafting_team_logo} alt="" className="team-logo" />
              <span className="draft-card-text">{pick.drafting_team_name}</span>
            </Link>
            <span className="via-draft-inline">
              via <Link to={`/team/${pick.original_team_id}`}>{pick.original_team_name}</Link>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Home() {
  const [players, setPlayers] = useState([]);
  const [trendingPlayers, setTrendingPlayers] = useState([]);
  const [countryPlayers, setCountryPlayers] = useState([]);
  const [draftPicks, setDraftPicks] = useState(null);
  const [draftCompleted, setDraftCompleted] = useState(false);
  const [topPlayers, setTopsPlayers] = useState(null);
  const [rankMocks, setRankMocks] = useState(null);
  const [topConsensusGraph, setTopConsensusGraph] = useState(null);
  const [draftOddsBoard, setDraftOddsBoard] = useState(null);
  const [loadingConsensusGraph, setLoadingConsensusGraph] = useState(true);
  const [loadingDraftOddsBoard, setLoadingDraftOddsBoard] = useState(true);
  const [country, setCountry] = useState("");
  const { draftYear } = useContext(DraftYearContext);

  const [loadingTopProspects, setLoadingTopProspects] = useState(true);
  const [loadingDraftResults, setLoadingDraftResults] = useState(true);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [draftYearChange, setDraftYearChange] = useState(true);
  const [prevDraftYear, setPrevDraftYear] = useState(null);

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await publicApi.get("https://api.seeip.org/geoip");
        const countryName = response.data.country;
        setCountry(countryName);
        setCountryPlayers(
          players
            .filter((player) => player.country_id === countryName.toLowerCase().replace(/\s/g, "_"))
            .slice(0, 4)
        );
      } catch (error) {
        console.error("Error fetching country:", error);
      }
    };
    fetchCountry();
  }, [players]);

  useEffect(() => {
    setLoadingTopProspects(true);
    publicApi
      .get("/players", { params: { draft_year: draftYear } })
      .then((response) => {
        setPlayers(response.data);
        setTopsPlayers(response.data.slice(0, TOP_PROSPECTS_BAR_COUNT));
        setLoadingTopProspects(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [draftYear]);

  useEffect(() => {
    setLoadingDraftResults(true);
    publicApi
      .get("/draft/picks", { params: { count: HOME_DRAFT_ORDER_COUNT, draft_year: draftYear } })
      .then((response) => {
        setDraftPicks(response.data);
        setDraftCompleted(response.data[0]?.player_id != null);
        setLoadingDraftResults(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [draftYear]);

  useEffect(() => {
    setLoadingLatest(true);
    publicApi
      .get("/shared/get_mocks_and_rankings", { params: { count: 5, draft_year: draftYear } })
      .then((response) => {
        setRankMocks(response.data);
        setLoadingLatest(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [draftYear]);

  useEffect(() => {
    setLoadingTrending(true);
    publicApi
      .get("/trending_consensus_rankings", {
        params: { count: 6, min_rank_start: 60, months: "3", draft_year: draftYear },
      })
      .then((response) => {
        setTrendingPlayers(response.data);
        setLoadingTrending(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [draftYear]);

  useEffect(() => {
    setLoadingConsensusGraph(true);
    publicApi
      .get("/consensus_rankings/top/graph", { params: { draft_year: draftYear, limit: 8 } })
      .then((response) => {
        setTopConsensusGraph(response.data);
        setLoadingConsensusGraph(false);
      })
      .catch((err) => {
        console.error("Error fetching top consensus graph:", err);
        setTopConsensusGraph(null);
        setLoadingConsensusGraph(false);
      });
  }, [draftYear]);

  useEffect(() => {
    if (draftCompleted) {
      setDraftOddsBoard(null);
      setLoadingDraftOddsBoard(false);
      return;
    }

    setLoadingDraftOddsBoard(true);
    publicApi
      .get("/draft_odds/board", {
        params: { draft_year: draftYear, pick_count: 5, top_prospects: 6, candidates_per_pick: 2 },
      })
      .then((response) => {
        setDraftOddsBoard(response.data);
        setLoadingDraftOddsBoard(false);
      })
      .catch((err) => {
        console.error("Error fetching draft odds board:", err);
        setDraftOddsBoard(null);
        setLoadingDraftOddsBoard(false);
      });
  }, [draftYear, draftCompleted]);

  useEffect(() => {
    if (prevDraftYear !== null && prevDraftYear !== draftYear) {
      setDraftYearChange(false);
    }
    setPrevDraftYear(draftYear);
  }, [draftYear]);

  const showDraftOdds =
    !draftCompleted &&
    (loadingDraftOddsBoard ||
      draftOddsBoard?.picks?.some((slot) => slot.candidates?.length > 0) ||
      draftOddsBoard?.prospects?.length > 0);

  const draftOrderFirstHalf = draftPicks?.slice(0, 8) ?? [];
  const draftOrderSecondHalf = draftPicks?.slice(8, 16) ?? [];

  function getIsLoading() {
    return (
      !(!loadingTopProspects && !loadingDraftResults && !loadingLatest && !loadingTrending) &&
      !draftYearChange
    );
  }

  return (
    <>
      <Helmet>
        <title>Home - Hockey Pipeline</title>
        <meta
          name="description"
          content="Track top NHL prospects with in-depth statistics, rankings, mock drafts, and player comparisons. Follow the journey of hockey prospects pre-draft and post-draft."
        />
        <link rel="canonical" href="https://hockeypipeline.com/" />
      </Helmet>

      <PageLoader loading={getIsLoading()} />

      <Container className="home-page" maxWidth="xl">
        {/* Hero: top prospects + quick nav */}
        <section className="home-hero home-panel">
          <div className="home-hero-top">
            <div>
              <Typography component="p" className="home-hero-eyebrow">
                {draftYear} NHL Draft
              </Typography>
              <Typography variant="h4" component="h1" className="home-hero-title">
                Top Prospects
              </Typography>
            </div>
            <nav className="home-hero-nav" aria-label="Quick links">
              <Link to="/consensus-rankings" className="home-hero-chip">
                Rankings
              </Link>
              <Link to="/players" className="home-hero-chip">
                All prospects
              </Link>
              <Link to="/media-rankings" className="home-hero-chip">
                Media boards
              </Link>
              <Link to="/player-comparison" className="home-hero-chip">
                Compare
              </Link>
              {showDraftOdds && (
                <Link to="/draft-odds" className="home-hero-chip">
                  Draft odds
                </Link>
              )}
            </nav>
          </div>

          <div className="home-prospect-strip home-prospect-strip--hero">
            {topPlayers?.map((player) => (
              <div key={player.player_id} className="home-prospect-strip-item">
                <PlayerCard playerData={player} miniView />
              </div>
            ))}
          </div>
        </section>

        {/* Main: analytics + sidebar feeds */}
        <Grid container spacing={2.5} className="home-main-grid">
          <Grid item xs={12} lg={8}>
            <div className="home-main-stack">
              <section className="home-panel home-panel--chart">
                <PanelHeader title="Consensus momentum" to="/consensus-rankings" />
                <HomeConsensusGraph
                  loading={loadingConsensusGraph}
                  payload={topConsensusGraph}
                  compact
                  embedded
                />
              </section>

              {showDraftOdds && (
                <HomeDraftOddsBoard
                  loading={loadingDraftOddsBoard}
                  payload={draftOddsBoard}
                  draftYear={draftYear}
                  compact
                />
              )}
            </div>
          </Grid>

          <Grid item xs={12} lg={4}>
            <aside className="home-sidebar">
              <section className="home-panel home-panel--feed home-panel--draft-order">
                <PanelHeader
                  title={draftCompleted ? "Draft results" : "Draft order"}
                  to="/draft-order"
                />
                <div className="home-feed-list home-feed-list--draft-order">
                  <div className="home-draft-order-col">
                    {draftOrderFirstHalf.map((pick) => (
                      <DraftOrderPick key={pick.pick} pick={pick} draftCompleted={draftCompleted} />
                    ))}
                  </div>
                  <div className="home-draft-order-col">
                    {draftOrderSecondHalf.map((pick) => (
                      <DraftOrderPick key={pick.pick} pick={pick} draftCompleted={draftCompleted} />
                    ))}
                  </div>
                </div>
              </section>

              <section className="home-panel home-panel--feed">
                <PanelHeader title="Latest media" to="/media-rankings" />
                <div className="home-feed-list">
                  {rankMocks?.map((rankMock) => (
                    <Link
                      key={rankMock.data_point_id}
                      to={`/${rankMock.rank_type === "mock_draft" ? "mock-draft" : "ranking"}/${rankMock.data_point_id}`}
                      className="rank-mock-mini"
                    >
                      <span className="rank-mock-title">{rankMock.title}</span>
                      <span className="rank-mock-subtitle">
                        {rankMock.rank_type === "ranking" ? "Ranking" : "Mock draft"}
                        <span className="rank-mock-divider" />
                        {formatDistance(subDays(new Date(rankMock.creation_date), 3), new Date(), {
                          addSuffix: true,
                        })}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>

              <section className="home-panel home-panel--feed">
                <PanelHeader title="Trending risers" to="/players" linkLabel="Browse" />
                <div className="home-feed-list">
                  {trendingPlayers?.map((player) => (
                    <div key={player.player_id} className="trending-player-card">
                      <span className="trending-player-rank">
                        <TrendDelta delta={player.delta} />
                      </span>
                      <Link to={`/player/${player.player_id}`} className="trending-player-name">
                        {player.player_name}
                      </Link>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </Grid>
        </Grid>

        {/* Regional prospects — compact footer strip */}
        {countryPlayers?.length > 0 && (
          <section className="home-panel home-country-section">
            <PanelHeader title={`Top from ${country}`} to="/players" />
            <div className="home-prospect-strip home-prospect-strip--compact">
              {countryPlayers.map((player) => (
                <div key={player.player_id} className="home-prospect-strip-item">
                  <PlayerCard playerData={player} miniView />
                </div>
              ))}
            </div>
          </section>
        )}
      </Container>
    </>
  );
}

export default Home;
